/**
 * Fog of war — a ROUND, tile-based vision with MEMORY (farm only). Three states per
 * tile, drawn one rect per tile so the edge is pixel/blocky like the reference:
 *   - in sight  (within CLEAR tiles, round/Euclidean): fully lit, fading over a few
 *     rings to the edge of sight;
 *   - explored  (seen before, now out of sight): a fixed dim + slight desaturation —
 *     you REMEMBER the terrain, but live things (mobs, loot, players) are hidden
 *     there, so it reads as a frozen snapshot rather than a live view;
 *   - unseen    (never in sight): total black.
 *
 * Explored tiles are stored as a 32×32-chunk bitmask (Block 5), kept in memory and
 * persisted to localStorage per world seed, so the discovered map survives reloads
 * and the minimap can read it cheaply. Lives in world space (child of the camera)
 * so it pans/zooms with the map; only on-screen tiles are drawn, and only when the
 * view or the hero tile changes.
 */
import { Container, Graphics } from 'pixi.js';
import { TILE } from './world';

export interface ViewBounds { minTx: number; minTy: number; maxTx: number; maxTy: number; }

export interface Fog {
  layer: Container;
  /** Per frame: hero TILE position + the visible tile rectangle. */
  update(heroTileX: number, heroTileY: number, view: ViewBounds): void;
  /** Has this tile ever been in sight? (drives the minimap + entity hiding.) */
  explored(tx: number, ty: number): boolean;
  /** Is this tile currently within clear sight? (entities render only here.) */
  inSightNow(tx: number, ty: number): boolean;
  destroy(): void;
}

// NOTE: there used to be a `REVEAL_ALL = true` constant here that made every tile
// count as explored and in sight, and made update() clear the layer and return. It
// silently disabled fog of war no matter what the player's setting said — and
// because the fog tests were guarded with `describe.skipIf(REVEAL_ALL)`, the suite
// stayed green the whole time it was dead. There is no switch of any kind now: fog
// is a mandatory rule, so every view that draws a map calls createFog()
// unconditionally (see views/farm.ts and views/dungeon.ts).

export const CLEAR = 7;           // radius of clear sight (tiles)
export const ACTIVE_RADIUS = 30;  // brief §5: 60×60 active zone — mobs/loot tick only within it
const EXPLORED_DIM = 0.55;        // darkness of a remembered (out-of-sight) tile
const CHUNK = 32;                 // explored bitmask chunk size (brief §5)
const CHUNK_BYTES = (CHUNK * CHUNK) / 8;
const FLUSH_MS = 4000;            // debounce persisting dirty chunks

const floorDiv = (n: number, d: number) => Math.floor(n / d);
const mod = (n: number, m: number) => ((n % m) + m) % m;

/**
 * Explored tiles as 32×32 chunks of 1-bit-per-tile, persisted per seed. Chunks are
 * created lazily and only re-saved when they change — a 683-wide map is thousands
 * of chunks, but a session only ever touches the handful the player walked through.
 */
class ExploredStore {
  private chunks = new Map<string, Uint8Array>();
  private dirty = new Set<string>();
  private lastFlush = 0;
  constructor(private readonly seed: string) {}

  private ckey(cx: number, cy: number) { return `${cx},${cy}`; }
  private storeKey(cx: number, cy: number) { return `fog:${this.seed}:${cx},${cy}`; }

  private chunk(cx: number, cy: number): Uint8Array {
    const k = this.ckey(cx, cy);
    let c = this.chunks.get(k);
    if (!c) {
      c = new Uint8Array(CHUNK_BYTES);
      try {
        const raw = localStorage.getItem(this.storeKey(cx, cy));
        if (raw) {
          const bin = atob(raw);
          for (let i = 0; i < CHUNK_BYTES && i < bin.length; i++) c[i] = bin.charCodeAt(i);
        }
      } catch { /* private mode / quota — memory-only is fine */ }
      this.chunks.set(k, c);
    }
    return c;
  }

  mark(tx: number, ty: number) {
    const cx = floorDiv(tx, CHUNK), cy = floorDiv(ty, CHUNK);
    const bit = mod(ty, CHUNK) * CHUNK + mod(tx, CHUNK);
    const c = this.chunk(cx, cy);
    const byte = bit >> 3, mask = 1 << (bit & 7);
    if (!(c[byte] & mask)) { c[byte] |= mask; this.dirty.add(this.ckey(cx, cy)); }
  }

  has(tx: number, ty: number): boolean {
    const cx = floorDiv(tx, CHUNK), cy = floorDiv(ty, CHUNK);
    // Lazy-load from storage on first touch (survives a reload); cached after.
    const c = this.chunk(cx, cy);
    const bit = mod(ty, CHUNK) * CHUNK + mod(tx, CHUNK);
    return (c[bit >> 3] & (1 << (bit & 7))) !== 0;
  }

  /** Persist dirty chunks, at most once per FLUSH_MS. */
  flush(nowMs: number, force = false) {
    if (!this.dirty.size) return;
    if (!force && nowMs - this.lastFlush < FLUSH_MS) return;
    this.lastFlush = nowMs;
    for (const k of this.dirty) {
      const c = this.chunks.get(k);
      if (!c) continue;
      const [cx, cy] = k.split(',').map(Number);
      try {
        let bin = '';
        for (let i = 0; i < c.length; i++) bin += String.fromCharCode(c[i]);
        localStorage.setItem(this.storeKey(cx, cy), btoa(bin));
      } catch { /* ignore quota */ }
    }
    this.dirty.clear();
  }
}

/** Darkness while a tile is IN SIGHT, by round distance → the blocky circle edge. */
function inSightAlpha(d: number): number {
  if (d <= CLEAR) return 0;
  if (d <= CLEAR + 1) return 0.20;
  if (d <= CLEAR + 2) return 0.40;
  return 0.55;   // last visible ring meets the explored dim
}

export function createFog(seed: string): Fog {
  const layer = new Container();
  layer.eventMode = 'none';
  const g = new Graphics();
  layer.addChild(g);
  const store = new ExploredStore(seed);
  let sig = '';
  let cx = 0, cy = 0;

  return {
    layer,
    explored: (tx, ty) => store.has(tx, ty),
    inSightNow: (tx, ty) => Math.hypot(tx - cx, ty - cy) <= CLEAR,
    update(hx, hy, view) {
      cx = Math.round(hx); cy = Math.round(hy);
      // Mark everything currently in sight as explored (remembered forever).
      const sightR = CLEAR + 2;
      for (let ty = cy - sightR; ty <= cy + sightR; ty++)
        for (let tx = cx - sightR; tx <= cx + sightR; tx++)
          if (Math.hypot(tx - cx, ty - cy) <= sightR) store.mark(tx, ty);
      store.flush(performance.now());

      // Redraw only when the hero tile or the visible rectangle changes.
      const s = `${cx},${cy},${view.minTx},${view.minTy},${view.maxTx},${view.maxTy}`;
      if (s === sig) return;
      sig = s;
      g.clear();

      const x0 = view.minTx - 1, x1 = view.maxTx + 1, y0 = view.minTy - 1, y1 = view.maxTy + 1;
      for (let ty = y0; ty <= y1; ty++) {
        for (let tx = x0; tx <= x1; tx++) {
          const d = Math.hypot(tx - cx, ty - cy);
          if (d <= CLEAR + 2) {
            const a = inSightAlpha(d);                          // currently in sight
            if (a > 0) g.rect(tx * TILE, ty * TILE, TILE, TILE).fill({ color: 0x000000, alpha: a });
          } else if (store.has(tx, ty)) {
            // Remembered: dim + a cool desaturating wash, so it reads as a frozen
            // snapshot (live entities over it are hidden by the entity layer).
            g.rect(tx * TILE, ty * TILE, TILE, TILE).fill({ color: 0x0a1420, alpha: EXPLORED_DIM });
          } else {
            g.rect(tx * TILE, ty * TILE, TILE, TILE).fill({ color: 0x000000, alpha: 1 });
          }
        }
      }
    },
    destroy() { store.flush(performance.now(), true); layer.destroy({ children: true }); },
  };
}
